/* eslint-disable react/forbid-prop-types */
import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Typography, IconButton, alpha, Tooltip, Collapse, Stack,
    Select, MenuItem, FormControl, InputLabel, Chip, Checkbox,
    ListItemText,
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Iconify from 'src/components/Iconify';
import { common } from 'src/theme/palette';
import FormQuickPreviewDrawer from './FormQuickPreviewDrawer';

/* ──────────────────────────────────────────────────────────────────────────
   PerFormBindingList
   ────────────────────────────────────────
   Drop-in replacement for SelectedFormsList that adds an expandable
   "Bind this form to…" row per item. Lets the practice user override the
   global treatment / treated-by selection on a per-form basis.

   Payload contract (BE additions in send_forms_serializers.py):
     per_form_treatment_ids:        { "<form_id>": [tid1, tid2] }
     per_form_practice_field_values: { "<form_id>": { "system:treated_by": {...} } }

   Both are optional. When omitted (or the per-form entry is absent),
   the BE falls back to the top-level treatment_ids / practice_field_values.

   Backward-compat: collapsed by default; the dialog functions exactly
   like the old SelectedFormsList unless the user opens an override.
   ────────────────────────────────────────────────────────────────────────── */

const TYPE_COLORS = {
    MEDICAL_HISTORY: '#855CF8',
    COMMUNICATION_CONSENT: '#078DEE',
    PHOTOGRAPHY_CONSENT: '#FF6C40',
    RADIOGRAPHY_CONSENT: '#00B8D9',
    SURVEY: '#FFAB00',
    TREATMENT_PLAN: '#00AB55',
    TREATMENT_CONSENT: '#B72136',
    CONSENT: '#078DEE',
    PATIENT_INTAKE: '#00AB55',
    REFERRAL: '#FFAB00',
    OTHER: '#637381',
};
const getTypeColor = (formType) => TYPE_COLORS[formType] || '#637381';

const PerFormBindingList = ({
    selectedForms,
    onRemove,
    onReorder,
    practiceId,
    treatmentOptions,
    practitionerOptions,
    perFormTreatmentIds,
    onPerFormTreatmentChange,
    perFormPracticeFieldValues,
    onPerFormPracticeFieldChange,
}) => {
    const [previewForm, setPreviewForm] = useState(null);
    const [expandedFormId, setExpandedFormId] = useState(null);

    const handleDragEnd = (result) => {
        if (!result.destination) return;
        if (result.source.index === result.destination.index) return;
        onReorder(result.source.index, result.destination.index);
    };

    const toggleExpand = (formId) => {
        setExpandedFormId((prev) => (prev === formId ? null : formId));
    };

    const overrideCount = useMemo(() => {
        let n = 0;
        Object.values(perFormTreatmentIds || {}).forEach((v) => {
            if (Array.isArray(v) && v.length) n += 1;
        });
        Object.values(perFormPracticeFieldValues || {}).forEach((v) => {
            if (v && Object.keys(v).length) n += 1;
        });
        return n;
    }, [perFormTreatmentIds, perFormPracticeFieldValues]);

    if (selectedForms.length === 0) {
        return (
            <Box
                sx={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    height: '100%', minHeight: 120,
                    border: '2px dashed', borderColor: 'divider',
                    borderRadius: '12px', p: 3, textAlign: 'center',
                }}
            >
                <Iconify icon="solar:document-add-bold" width={32} sx={{ color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '13px', mb: 0.5 }}>
                    No forms selected yet
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.5 }}>
                    Browse forms on the left and click + to add them here.
                </Typography>
            </Box>
        );
    }

    return (
        <>
            {overrideCount > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                    <Chip
                        size="small"
                        icon={<Iconify icon="eva:flash-fill" width={11} />}
                        label={`${overrideCount} per-form override${overrideCount === 1 ? '' : 's'}`}
                        sx={{
                            height: 18, fontSize: '10px', fontWeight: 700,
                            color: '#7635DC',
                            bgcolor: alpha('#7635DC', 0.08),
                            '& .MuiChip-icon': { color: '#7635DC', ml: 0.5 },
                        }}
                    />
                </Box>
            )}
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="per-form-binding-list">
                    {(provided, snapshot) => (
                        <Box
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            sx={{
                                display: 'flex', flexDirection: 'column', gap: 0.75,
                                height: '100%', overflowY: 'auto', borderRadius: '10px',
                                transition: 'background 0.2s ease',
                                bgcolor: snapshot.isDraggingOver ? alpha(common.mintyZest, 0.04) : 'transparent',
                                p: snapshot.isDraggingOver ? 0.5 : 0,
                            }}
                        >
                            {selectedForms.map((form, idx) => {
                                const color = getTypeColor(form.form_type);
                                const formIdKey = String(form.id);
                                const isExpanded = expandedFormId === form.id;
                                const overrideTreatments = perFormTreatmentIds?.[formIdKey] || [];
                                const overrideTreatedBy = perFormPracticeFieldValues?.[formIdKey]?.['system:treated_by']?.value || '';
                                const hasOverride = overrideTreatments.length > 0 || Boolean(overrideTreatedBy);
                                return (
                                    <Draggable key={formIdKey} draggableId={formIdKey} index={idx}>
                                        {(dragProvided, dragSnapshot) => (
                                            <Box
                                                ref={dragProvided.innerRef}
                                                {...dragProvided.draggableProps}
                                                sx={{
                                                    borderRadius: '10px',
                                                    border: '1px solid',
                                                    borderColor: (() => {
                                                        if (dragSnapshot.isDragging) return alpha(common.mintyZest, 0.5);
                                                        if (hasOverride) return alpha('#7635DC', 0.3);
                                                        return 'divider';
                                                    })(),
                                                    bgcolor: dragSnapshot.isDragging
                                                        ? alpha(common.mintyZest, 0.06)
                                                        : '#fff',
                                                    boxShadow: dragSnapshot.isDragging
                                                        ? '0 4px 14px rgba(0,0,0,0.12)' : 'none',
                                                    transition: dragSnapshot.isDragging ? 'none' : 'all 0.15s ease',
                                                    '&:hover': { borderColor: alpha(common.mintyZest, 0.3) },
                                                }}
                                            >
                                                {/* Row header (matches old SelectedFormsList look) */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.75 }}>
                                                    <Box
                                                        {...dragProvided.dragHandleProps}
                                                        sx={{
                                                            display: 'flex', alignItems: 'center', cursor: 'grab',
                                                            color: '#C4CDD5', flexShrink: 0,
                                                            '&:hover': { color: '#637381' },
                                                            '&:active': { cursor: 'grabbing' },
                                                        }}
                                                    >
                                                        <Iconify icon="mdi:drag-vertical" width={18} />
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            width: 22, height: 22, borderRadius: '6px',
                                                            bgcolor: alpha(color, 0.1), color,
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            fontSize: '11px', fontWeight: 700, flexShrink: 0,
                                                        }}
                                                    >
                                                        {idx + 1}
                                                    </Box>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="subtitle2" noWrap sx={{ fontSize: '12px', fontWeight: 600 }}>
                                                            {form.name}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption" noWrap
                                                            sx={{ fontSize: '10px', color, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}
                                                        >
                                                            {form.form_type?.replace(/_/g, ' ')}
                                                        </Typography>
                                                    </Box>
                                                    {hasOverride && (
                                                        <Chip
                                                            size="small"
                                                            label="Custom"
                                                            sx={{
                                                                height: 18, fontSize: '9.5px', fontWeight: 700,
                                                                color: '#7635DC', bgcolor: alpha('#7635DC', 0.08),
                                                                textTransform: 'uppercase', letterSpacing: '0.4px',
                                                            }}
                                                        />
                                                    )}
                                                    <Tooltip title={isExpanded ? 'Hide bindings' : 'Bind treatment / treated by'} arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => toggleExpand(form.id)}
                                                            aria-label={isExpanded ? 'Hide form bindings' : 'Show form bindings'}
                                                            sx={{
                                                                p: 0.5,
                                                                color: isExpanded ? '#7635DC' : 'text.disabled',
                                                                '&:hover': { color: '#7635DC', bgcolor: alpha('#7635DC', 0.06) },
                                                            }}
                                                        >
                                                            <Iconify icon={isExpanded ? 'eva:chevron-up-fill' : 'eva:options-2-outline'} width={14} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Preview form" arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setPreviewForm(form)}
                                                            sx={{ p: 0.5, color: 'text.disabled', '&:hover': { color: '#078DEE', bgcolor: alpha('#078DEE', 0.06) } }}
                                                            aria-label={`Preview ${form.name || 'form'}`}
                                                        >
                                                            <Iconify icon="eva:eye-outline" width={14} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onRemove(form.id)}
                                                        aria-label={`Remove ${form.name || 'form'}`}
                                                        sx={{ p: 0.5, color: 'text.disabled', '&:hover': { color: '#B72136', bgcolor: alpha('#B72136', 0.06) } }}
                                                    >
                                                        <Iconify icon="eva:close-fill" width={14} />
                                                    </IconButton>
                                                </Box>

                                                {/* Expandable per-form bindings */}
                                                <Collapse in={isExpanded} timeout="auto">
                                                    <Box
                                                        sx={{
                                                            px: 1.5, pb: 1.25, pt: 0.5,
                                                            borderTop: '1px dashed',
                                                            borderColor: alpha('#7635DC', 0.2),
                                                            bgcolor: alpha('#7635DC', 0.02),
                                                        }}
                                                    >
                                                        <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#7635DC', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75 }}>
                                                            Override for this form only
                                                        </Typography>
                                                        <Stack spacing={1}>
                                                            <FormControl size="small" fullWidth>
                                                                <InputLabel id={`treated-by-${form.id}`} sx={{ fontSize: '11px' }}>
                                                                    Treated by
                                                                </InputLabel>
                                                                <Select
                                                                    labelId={`treated-by-${form.id}`}
                                                                    label="Treated by"
                                                                    value={overrideTreatedBy}
                                                                    onChange={(e) => onPerFormPracticeFieldChange(form.id, {
                                                                        ...(perFormPracticeFieldValues?.[formIdKey] || {}),
                                                                        'system:treated_by': e.target.value
                                                                            ? { value: e.target.value, label: practitionerOptions.find((p) => String(p.id) === String(e.target.value))?.label || '' }
                                                                            : null,
                                                                    })}
                                                                    displayEmpty
                                                                    sx={{ fontSize: '11.5px', borderRadius: '8px', bgcolor: '#fff' }}
                                                                >
                                                                    <MenuItem value="" sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                                                        — use global —
                                                                    </MenuItem>
                                                                    {practitionerOptions.map((p) => (
                                                                        <MenuItem key={p.id} value={String(p.id)} sx={{ fontSize: '11.5px' }}>
                                                                            {p.label}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                            {treatmentOptions.length > 0 && (
                                                                <FormControl size="small" fullWidth>
                                                                    <InputLabel id={`treatments-${form.id}`} sx={{ fontSize: '11px' }}>
                                                                        Treatments
                                                                    </InputLabel>
                                                                    <Select
                                                                        labelId={`treatments-${form.id}`}
                                                                        label="Treatments"
                                                                        multiple
                                                                        value={overrideTreatments.map(String)}
                                                                        onChange={(e) => {
                                                                            const v = e.target.value;
                                                                            const next = Array.isArray(v) ? v.map(Number) : [];
                                                                            onPerFormTreatmentChange(form.id, next);
                                                                        }}
                                                                        renderValue={(selected) => selected.length === 0
                                                                            ? '— use global —'
                                                                            : `${selected.length} selected`}
                                                                        displayEmpty
                                                                        sx={{ fontSize: '11.5px', borderRadius: '8px', bgcolor: '#fff' }}
                                                                    >
                                                                        {treatmentOptions.map((t) => (
                                                                            <MenuItem key={t.id} value={String(t.id)} sx={{ fontSize: '11.5px', py: 0.5 }}>
                                                                                <Checkbox checked={overrideTreatments.map(String).includes(String(t.id))} size="small" />
                                                                                <ListItemText
                                                                                    primary={t.name}
                                                                                    secondary={t.category}
                                                                                    primaryTypographyProps={{ fontSize: '11.5px' }}
                                                                                    secondaryTypographyProps={{ fontSize: '10px' }}
                                                                                />
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>
                                                            )}
                                                            <Typography sx={{ fontSize: '10px', color: 'text.secondary', lineHeight: 1.5 }}>
                                                                Leave blank to inherit the global selection. Per-form overrides only apply to THIS form’s PDF.
                                                            </Typography>
                                                        </Stack>
                                                    </Box>
                                                </Collapse>
                                            </Box>
                                        )}
                                    </Draggable>
                                );
                            })}
                            {provided.placeholder}
                        </Box>
                    )}
                </Droppable>
            </DragDropContext>

            {previewForm && (
                <FormQuickPreviewDrawer
                    open={Boolean(previewForm)}
                    onClose={() => setPreviewForm(null)}
                    practiceId={practiceId}
                    form={previewForm}
                    typeColor={getTypeColor(previewForm.form_type)}
                    typeLabel={(previewForm.form_type || '').replace(/_/g, ' ')}
                    isSelected
                    onRemove={(f) => onRemove(f.id)}
                />
            )}
        </>
    );
};

PerFormBindingList.propTypes = {
    selectedForms: PropTypes.array.isRequired,
    onRemove: PropTypes.func.isRequired,
    onReorder: PropTypes.func.isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    treatmentOptions: PropTypes.array,
    practitionerOptions: PropTypes.array,
    perFormTreatmentIds: PropTypes.object,
    onPerFormTreatmentChange: PropTypes.func.isRequired,
    perFormPracticeFieldValues: PropTypes.object,
    onPerFormPracticeFieldChange: PropTypes.func.isRequired,
};

PerFormBindingList.defaultProps = {
    practiceId: null,
    treatmentOptions: [],
    practitionerOptions: [],
    perFormTreatmentIds: {},
    perFormPracticeFieldValues: {},
};

export default PerFormBindingList;
